import { Component, Input, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AgGridAngular } from "ag-grid-angular";
import {
    UntypedFormGroup,
    FormsModule,
    ReactiveFormsModule,
} from "@angular/forms";

import {
    ClientSideRowModelModule,
    ModuleRegistry,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    GridApi,
    ColDef,
    TextEditorModule,
    LargeTextEditorModule,
    CellEditingStartedEvent,
    CellEditingStoppedEvent,
    CellClickedEvent,
    NumberEditorModule,
    RowStyleModule,
    RenderApiModule,
    ClientSideRowModelApiModule,
    NewValueParams,
    ScrollApiModule,
    HighlightChangesModule,
} from "ag-grid-community";

import {
    NgbModal,
    NgbModalOptions,
    NgbActiveModal,
} from "@ng-bootstrap/ng-bootstrap";


import { CreateCommentModalComponent } from "./components/create-comment-modal/create-comment-modal.component";
import { ActionsComponent } from "../contract-comments/components/actions/actions.component";
import { CommentCellRendererComponent, stripHtml } from "../comment-cell-renderer/comment-cell-renderer.component";
import { CommentEditModalComponent } from "../comment-edit-modal/comment-edit-modal.component";
import { ContractApiService } from "src/app/core/api/contract-api.service";
import { ContractComment } from "src/app/core/models/contractComment";
import { formatDate } from "@angular/common";
import { AuthenticationService } from "src/app/core/services/authentiation.service";
import { take } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { SpinnerService } from "src/app/core/services/spinner.service";

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    TextEditorModule,
    LargeTextEditorModule,
    NumberEditorModule,
    RowStyleModule,
    RenderApiModule,
    ClientSideRowModelApiModule,
    ScrollApiModule,
    HighlightChangesModule
]);

@Component({
    selector: "app-contract-comments",
    standalone: true,
    imports: [
        AgGridAngular,
        TranslatePipe,
        FormsModule,
        ReactiveFormsModule,
    ],
    templateUrl: "./contract-comments.component.html",
    styleUrl: "./contract-comments.component.scss",
})
export class ContractCommentsComponent implements OnInit {
     @Input() contractId: number;
    public contractCommentsForm: UntypedFormGroup;
    public isCellEditing = false;
    // public contractId: number;
    public contractComments: ContractComment[] = [];
    public commentGridTitles: any = {};
    public colDefs: ColDef<ContractComment>[] = [];
    public currentAdminName: string;
    getRowStyle: any;
    gridApi: GridApi<ContractComment>;
    activeModal: NgbActiveModal;

    constructor(
        private route: ActivatedRoute,
        private modalService: NgbModal,
        private contractApiService: ContractApiService,
        private translateService: TranslateService,
        private authenticationService: AuthenticationService,
        private toastrService: ToastrService,
        private spinner: SpinnerService
    ) {}

    defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true, // Adds filter input fields at the top
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
    };

        public validationOnEmptyCell(event: NewValueParams<ContractComment, any, any>) {
            // Revert to previous value
            event.data[event.colDef.field as any] = event.oldValue;
            this.gridApi.refreshCells({ force: true });

                // Keep focus on the current cell (don't move to next tab)
                this.gridApi.startEditingCell({
                    rowIndex: event.node.rowIndex,
                    colKey: event.colDef.field,
                });
        }


        public updateField(eventData: NewValueParams<ContractComment, any, any>) {
            this.contractApiService
                .updateContractComment(eventData.data)
                .pipe(take(1))
                .subscribe({
                    next: () => {
                        this.translateService
                            .get("contractComment.form.updateComment", {
                                field: eventData.colDef.headerName,
                            })
                            .subscribe((res) => {
                                this.toastrService.success(res)
                            this.loadContractCommentsList()
                            });
                    },
                    error: () => {
                        this.translateService
                            .get("contractComment.form.updateError", {
                                field: eventData.colDef.headerName,
                            })
                            .subscribe((res) => this.toastrService.error(res));
                    },
                });
        }

    private openCommentEditor(event: CellClickedEvent<ContractComment>): void {
        const options: NgbModalOptions = { centered: true, backdrop: true, size: 'lg' };
        const modalRef = this.modalService.open(CommentEditModalComponent, options);
        modalRef.componentInstance.initialValue = event.value ?? '';
        modalRef.result.then((newValue: string) => {
            if (newValue === event.value) return;
            const updated: ContractComment = { ...event.data, comment: newValue };
            event.node.setDataValue('comment', newValue);
            this.contractApiService.updateContractComment(updated).pipe(take(1)).subscribe({
                next: () => {
                    this.translateService.get('contractComment.form.updateComment', { field: event.colDef.headerName })
                        .subscribe(res => {
                            this.toastrService.success(res);
                            this.loadContractCommentsList();
                        });
                },
                error: () => {
                    event.node.setDataValue('comment', event.value);
                    this.translateService.get('contractComment.form.updateError', { field: event.colDef.headerName })
                        .subscribe(res => this.toastrService.error(res));
                },
            });
        }).catch(() => {});
    }

    autoGroupColumnDef: ColDef = {
        minWidth: 200,
    };

    private initializeColumnDefs() {
        this.colDefs = [
                    {
                field: "createdAt",
                headerName: this.commentGridTitles.createdDate,
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                width: 150,
                sort: "desc",
            },
            {
                field: "adminName",
                headerName: this.commentGridTitles.adminName,
                filter: "agTextColumnFilter",
            },
            {
                field: "comment",
                headerName: this.commentGridTitles.comment,
                filter: "agTextColumnFilter",
                editable: false,
                flex: 1,
                minWidth: 100,
                sortable: false,
                resizable: false,
                cellRenderer: CommentCellRendererComponent,
                filterValueGetter: (params) => stripHtml(params.data?.comment),
                onCellClicked: (event: CellClickedEvent<ContractComment>) => this.openCommentEditor(event),
            },

            {
                headerName: this.commentGridTitles.actions,
                cellRenderer: ActionsComponent,
                filter: false,
                width: 120,
                pinned:"right",
                resizable: false,
                lockPinned: true,
                lockPosition: true,

            },
        ];
    }
    ngOnInit() {
        this.authenticationService.currentUser.subscribe((user) => {
            if (user) {
                this.currentAdminName = user.name;
            }
        });
        // this.route.parent?.paramMap.subscribe((params) => {
        //     this.contractId = Number(params.get("contractId"));
        // });
        this.translateService
            .stream("contractComment.form")
            .subscribe((res) => {
                this.commentGridTitles = res;
                this.initializeColumnDefs();
            });

        this.loadContractCommentsList();
    }

    private loadContractCommentsList() {
        // this.spinner.show();
        this.contractApiService
            .getAllContractCommentsByContractId(this.contractId)
            .subscribe({
                next: (res) => {
                    this.contractComments = res;
                    // this.spinner.hide();
                },
                error: () => {},
            });
    }

    onGridReady(params: any) {
        this.gridApi = params.api;
        this.loadInitialData()

    }

    loadInitialData(): void {
        this.gridApi.setGridOption("rowData", this.contractComments);
    }


    onCellEditingStarted(event: CellEditingStartedEvent) {
        this.isCellEditing = true;

        this.getRowStyle = (params: any) => {
            if (params.node.id === event.node.id) {
                return { background: "#b0d7efc9" };
            }

            return null;
        };
    }
    onCellEditingStopped(event: CellEditingStoppedEvent) {
        this.isCellEditing = false;
        this.getRowStyle = (params: any) => {
            if (params.node.id === event.node.id) {
                return { background: "" };
            }

            return null;
        };
    }

    onSave() {
        this.activeModal.close("the modal is closed succussfully");
    }

    public openModal() {
        const options: NgbModalOptions = {
            centered: true,
            backdrop: true,
            size: "lg",
        };

        const modalRef = this.modalService.open(
            CreateCommentModalComponent,
            options
        );
        modalRef.componentInstance.contractId = this.contractId;
        modalRef.componentInstance.currentAdminName = this.currentAdminName

        modalRef.result.then(
            (result) => {
             this.gridApi.applyTransaction({add:[result]})

            });
    }
}
